import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Spinner, Alert, Input,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import { attachTeamResponsibilitiesBulk } from '../store/team/actions';

// Endpoints that need scoping to the active organization (super-admin sessions
// can otherwise hit Global View where these endpoints either bleed cross-org
// data or return nothing). The {orgId} placeholder is replaced at request time.
const ORG_PARAM = (orgId, prefix = '?') =>
  orgId == null ? '' : `${prefix}organizationId=${orgId}`;

const targetTypeMeta = {
  LOCATION: {
    endpoint: (orgId) =>
      `organization-location/get-all?hierarchy=false${ORG_PARAM(orgId, '&')}`,
    extract: (data) => {
      // The endpoint groups locations under an org wrapper:
      //   response: [{ organization, locations: [{ id, locationName, subLocations }, ...] }, ...]
      // With hierarchy=false subLocations are typically empty, but we still
      // walk the tree defensively in case a sibling caller flips the flag.
      const out = [];
      const pushLoc = (l) => {
        if (!l || l.id == null) return;
        out.push({
          id: l.id,
          name: l.locationName || l.name,
          subtitle: l.locationAddress,
        });
        if (Array.isArray(l.subLocations)) {
          for (const child of l.subLocations) pushLoc(child);
        }
      };
      const wrappers = Array.isArray(data?.response) ? data.response : [];
      for (const w of wrappers) {
        if (Array.isArray(w?.locations)) {
          for (const loc of w.locations) pushLoc(loc);
        } else if (w && typeof w === 'object' && w.locationName) {
          pushLoc(w);
        }
      }
      return out;
    },
  },
  ASSET: {
    // asset/all returns [{ organization, assets: [{ id, assetName, assetNumber, subAssets: [...] }, ...] }, ...]
    // — descend into wrapper.assets and recursively flatten subAssets. Org id
    // is required (Reactive.jsx always passes it); without it the response is empty.
    endpoint: (orgId) => `asset/all?hierarchy=false${ORG_PARAM(orgId, '&')}`,
    extract: (data) => {
      const out = [];
      const pushAsset = (a) => {
        if (!a || a.id == null) return;
        out.push({
          id: a.id,
          name: a.assetName || a.name,
          subtitle: a.assetNumber,
        });
        if (Array.isArray(a.subAssets)) {
          for (const child of a.subAssets) pushAsset(child);
        }
        if (Array.isArray(a.children)) {
          for (const child of a.children) pushAsset(child);
        }
      };
      const wrappers = Array.isArray(data?.response) ? data.response : [];
      for (const w of wrappers) {
        if (Array.isArray(w?.assets)) {
          for (const a of w.assets) pushAsset(a);
        } else if (w && typeof w === 'object' && (w.assetName || w.assetNumber)) {
          // Fallback: flat shape
          pushAsset(w);
        }
      }
      return out;
    },
  },
  ASSET_TYPE: {
    endpoint: (orgId) =>
      `organization-asset-type/getAll${ORG_PARAM(orgId)}`,
    extract: (data) => {
      const list = data?.response || [];
      return list.map((t) => ({
        id: t.id,
        name: t.name,
        subtitle: t.description,
      }));
    },
  },
  WORK_ORDER_CATEGORY: {
    endpoint: (orgId) =>
      `organization-category/getAll${ORG_PARAM(orgId)}`,
    extract: (data) => {
      const list = data?.response || [];
      return list.map((c) => ({
        id: c.id,
        name: c.name,
        subtitle: c.description,
      }));
    },
  },
};

/**
 * Multi-select picker for assigning many targets of one type to a team in a
 * single bulk request. Already-attached options are pre-checked and disabled
 * so the user only adds new ones — keeps the action semantics simple.
 */
const TeamResponsibilityPicker = ({
  isOpen,
  toggle,
  teamId,
  targetType,
  attachedIds = new Set(),
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { responsibilitySaving, responsibilityError } = useSelector((s) => s.Team);
  const activeOrganizationId = useSelector((s) => s.ActiveContext?.activeOrganizationId);

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen || !targetType) return;
    let cancelled = false;
    const meta = targetTypeMeta[targetType];
    if (!meta) return;

    setLoading(true);
    setError(null);
    setSelected(new Set());
    setSearch('');
    const url = typeof meta.endpoint === 'function'
      ? meta.endpoint(activeOrganizationId)
      : meta.endpoint;
    api
      .get(url)
      .then(({ data }) => {
        if (cancelled) return;
        setItems(meta.extract(data));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Failed to load options');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, targetType, activeOrganizationId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((it) => {
      return (it.name || '').toLowerCase().includes(term)
        || (it.subtitle || '').toLowerCase().includes(term);
    });
  }, [items, search]);

  const toggleId = (id) => {
    if (attachedIds.has(id)) return; // pre-attached options are read-only
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    if (selected.size === 0) return;
    dispatch(attachTeamResponsibilitiesBulk(
      { teamId, targetType, targetIds: Array.from(selected) },
      (err) => {
        if (!err) {
          toggle();
        }
      }
    ));
  };

  const heading = (() => {
    switch (targetType) {
      case 'LOCATION':
        return t('teams.responsibilities.attachLocation', 'Assign location');
      case 'ASSET':
        return t('teams.responsibilities.attachAsset', 'Assign asset');
      case 'ASSET_TYPE':
        return t('teams.responsibilities.attachAssetType', 'Assign asset type');
      case 'WORK_ORDER_CATEGORY':
        return t('teams.responsibilities.attachWorkOrderCategory', 'Assign work order category');
      default:
        return '';
    }
  })();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
      <ModalHeader toggle={toggle}>{heading}</ModalHeader>
      <ModalBody>
        {responsibilityError && <Alert color="danger">{responsibilityError}</Alert>}
        {error && <Alert color="warning">{error}</Alert>}

        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('teams.responsibilities.searchPlaceholder', 'Search...')}
          className="mb-2"
        />

        {attachedIds.size > 0 && (
          <div className="text-muted small mb-2">
            {t('teams.responsibilities.preselectedHint', 'Already-assigned options are pre-selected and cannot be changed here.')}
          </div>
        )}

        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-muted text-center py-3">
            {t('teams.responsibilities.noOptions', 'No options match your search.')}
          </div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {filtered.map((item) => {
              const isAttached = attachedIds.has(item.id);
              const isChecked = isAttached || selected.has(item.id);
              const inputId = `team-resp-opt-${item.id}`;
              return (
                <label
                  key={item.id}
                  htmlFor={inputId}
                  className="d-flex align-items-center gap-2 py-2 border-bottom"
                  style={{ cursor: isAttached ? 'not-allowed' : 'pointer', opacity: isAttached ? 0.6 : 1, marginBottom: 0 }}
                >
                  <Input
                    id={inputId}
                    type="checkbox"
                    className="m-0"
                    checked={isChecked}
                    disabled={isAttached}
                    onChange={() => toggleId(item.id)}
                  />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-medium text-truncate" title={item.name}>
                      {item.name}
                    </div>
                    {item.subtitle && (
                      <div className="text-muted small text-truncate" title={item.subtitle}>
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={responsibilitySaving}>
          {t('shared.cancel', 'Cancel')}
        </Button>
        <Button
          color="primary"
          onClick={handleAdd}
          disabled={selected.size === 0 || responsibilitySaving}
        >
          {responsibilitySaving
            ? <Spinner size="sm" />
            : <><FeatherIcon icon="plus" size={12} className="me-1" />{t('teams.responsibilities.bulkAddCount', 'Add ({{count}})', { count: selected.size })}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TeamResponsibilityPicker;
